import React, { useCallback, useRef, useState, useLayoutEffect } from "react"
import classnames from "classnames"

import TouchpointButton from "../components/touchpoint-button"
import MainLayout from "../components/main-layout"
import SEO from "../components/seo"

import UserIcon from "../images/user.inline.svg"

import * as styles from "./industry.module.css"
import Roles from "../components/roles-bar"
import useDeviceType from "../hooks/useDeviceType"
import { Link } from "gatsby"

interface TouchpointPageTemplatePageContext {
  type: "customer" | "employee"
  name: string
  solution?: {
    slug: string
    description: string
  }
  touchpoints: { name: string; logo: string; description: string }[]
  industries: { name: string; logo: string }[]
}

export default function TouchpointPageTemplate({
  pageContext,
}: {
  pageContext: TouchpointPageTemplatePageContext
}) {
  const DeviceType = useDeviceType()

  const mouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const targetWidth = e.currentTarget.clientWidth
      const targetXPos = e.currentTarget.getBoundingClientRect().left
      const userIcon = userIconRef.current

      const containerPadding = document.body.clientWidth * 0.1

      const marginLeft = 50
      const leftOffset =
        (TouchpointsContainerRef.current
          ? TouchpointsContainerRef.current.getBoundingClientRect().left
          : 0) +
        containerPadding -
        marginLeft

      if (userIcon) {
        userIcon.style.left =
          targetXPos + targetWidth / 2 - leftOffset / 2 + "px"
      }
    },
    []
  )

  const [touchPointContainerWidth, setTouchPointContainerWidth] = useState(0)

  const mouseLeave = useCallback(() => {
    const userIcon = userIconRef.current

    if (userIcon) {
      userIcon.style.left = 0 + "px"
    }
  }, [])

  const userIconRef = useRef<HTMLDivElement>(null)
  const TouchpointsContainerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    TouchpointsContainerRef.current &&
      setTouchPointContainerWidth(TouchpointsContainerRef.current.clientWidth)
  }, [TouchpointsContainerRef, setTouchPointContainerWidth])

  useLayoutEffect(() => {
    const pageResize = () => {
      TouchpointsContainerRef.current &&
        setTouchPointContainerWidth(TouchpointsContainerRef.current.clientWidth)
    }

    window.addEventListener("resize", pageResize)

    return () => {
      window.removeEventListener("resize", pageResize)
    }
  }, [])

  const mode = pageContext.type

  return (
    <MainLayout HasFooter mode={mode}>
      <>
        <div
          className={styles.contentContainer}
          style={{ width: "100%", position: "relative" }}
        >
          {pageContext.solution && (
            <div className={styles.industryPageLink}>
              <h3>{pageContext.solution.description}</h3>
              <Link
                className={classnames(styles.button, styles.fill)}
                to={pageContext.solution.slug}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div>
                  Learn about PXP <span>{pageContext.name}</span>
                </div>
              </Link>
            </div>
          )}
          <div className={styles.container}>
            <div className={styles.mainContainer}>
              <div className={styles.explain}>
                <span className={classnames(styles.name, "header-font")}>
                  {pageContext.name} Journey
                </span>
                <span className={styles.bodyFont}>Select Touchpoint</span>
                <div ref={userIconRef} className={styles.userIcon}>
                  <UserIcon />
                </div>
              </div>
              <div ref={TouchpointsContainerRef} className={styles.journey}>
                <div className={styles.journeyLine}></div>
                {pageContext.touchpoints
                  .slice(
                    0,
                    DeviceType === "tablet" || DeviceType === "mobile"
                      ? 4
                      : pageContext.touchpoints.length
                  )
                  .map((x, i) => (
                    <TouchpointButton
                      style={{
                        width:
                          touchPointContainerWidth /
                            (DeviceType === "mobile" || DeviceType === "tablet"
                              ? 4
                              : pageContext.touchpoints.length) +
                          "px",
                        maxWidth: 170,
                        maxHeight: 170,
                        height:
                          touchPointContainerWidth /
                            (DeviceType === "mobile" || DeviceType === "tablet"
                              ? 4
                              : pageContext.touchpoints.length) +
                          "px",
                      }}
                      key={i}
                      indicatorSide={i % 2 === 0 ? "bottom" : "top"}
                      name={x.name}
                      logo={x.logo}
                      link={`/${mode}/industry/${pageContext.name}/touchpoint/${x.name}`}
                      onMouseEnter={mouseEnter}
                      onMouseLeave={mouseLeave}
                      mode={mode}
                      last={i === pageContext.touchpoints.length - 1}
                      first={i === 0}
                      description={x.description}
                    />
                  ))}
                {(DeviceType === "tablet" || DeviceType === "mobile") && (
                  <div className={styles.journeyBottom}>
                    {pageContext.touchpoints
                      .slice(4, pageContext.touchpoints.length)
                      .map((x, i) => (
                        <TouchpointButton
                          style={{
                            width: touchPointContainerWidth / 4 + "px",
                            maxWidth: 170,
                            maxHeight: 170,
                            height: touchPointContainerWidth / 4 + "px",
                          }}
                          key={i}
                          indicatorSide={i % 2 === 0 ? "top" : "bottom"}
                          name={x.name}
                          logo={x.logo}
                          link={`/${mode}/industry/${pageContext.name}/touchpoint/${x.name}`}
                          onMouseEnter={mouseEnter}
                          onMouseLeave={mouseLeave}
                          mode={mode}
                          last={i === pageContext.touchpoints.length - 1}
                          first={i === 0}
                          description={x.description}
                          descriptionSide="top"
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    </MainLayout>
  )
}

export const Head = ({
  pageContext,
}: {
  pageContext: TouchpointPageTemplatePageContext
}) => (
  <SEO title={`${pageContext.name} Industry Customer Feedback Management`} />
)
